import Phaser from 'phaser';
import { Entity, type EntityOptions } from '@/entities/Entity';
import { Hitbox, type CombatSystem, type Team } from '@/systems/Combat';
import { boxFromCenter } from '@/systems/box';
import type { BossBrain } from '@/systems/BossBrain';

/** HitSpec per attackType emitted by the brain. */
const BOSS_HIT_SPECS: Record<string, { damage: number; knockback: number; hitstun: number }> = {
  hook_lunge: { damage: 14, knockback: 70, hitstun: 28 },
  hook_pull: { damage: 10, knockback: -50, hitstun: 22 },
  barrel_throw: { damage: 12, knockback: 60, hitstun: 24 },
  swing: { damage: 16, knockback: 80, hitstun: 32 },
};

const DEFAULT_HIT_SPEC = { damage: 12, knockback: 60, hitstun: 24 };

const ATTACK_ACTIVE_FRAMES = 8;
const ATTACK_HB_W = 80;
const ATTACK_HB_H = 100;

/**
 * BossEnemy — Phaser adapter for data-driven stage bosses.
 *
 * Movement and attacks are driven by a pluggable BossBrain (pure, testable).
 * Phase transitions live inside the brain — this class is the thin Phaser glue.
 *
 * Scene wire-up:
 *   - Emits 'boss-wants-summon' on the scene when the brain requests an add-wave.
 *   - Exposes hpFraction so StageScene can feed the HUD boss bar.
 */
export class BossEnemy extends Entity {
  readonly team: Team = 'enemy';

  private readonly brain: BossBrain;

  private target?: Entity;

  private combatSystem?: CombatSystem;

  private aggressionScale = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, brain: BossBrain, opts: EntityOptions) {
    super(scene, x, y, opts);
    this.brain = brain;
  }

  /** Current HP / maxHp (0..1). Used by StageScene to feed the HUD boss bar. */
  get hpFraction(): number {
    return this.health.fraction;
  }

  setTarget(target: Entity): void {
    this.target = target;
  }

  setCombatSystem(cs: CombatSystem): void {
    this.combatSystem = cs;
  }

  setAggression(scale: number): void {
    this.aggressionScale = Math.max(0, Math.min(1, scale));
  }

  update(): void {
    this.tickHitstun();
    this.tickJump();
    if (this.tickThrown()) return;
    if (this.tickJuggleArc()) return;

    if (!this.target) {
      this.syncAnim({ moving: false });
      return;
    }

    const intent = this.brain.tick({
      selfX: this.x,
      selfY: this.y,
      targetX: this.target.x,
      targetY: this.target.y,
      isHitstunned: this.isHitstunned,
      isKO: this.isKO,
      isBeingThrown: this.isBeingThrown,
      canAttack: true,
      aggressionScale: this.aggressionScale,
      orbitOffsetX: 0,
      orbitOffsetY: 0,
      hpFraction: this.hpFraction,
    });

    this.setFacing(intent.facing);

    if (intent.moveX !== 0 || intent.moveY !== 0) {
      this.x = Phaser.Math.Clamp(
        this.x + intent.moveX * this.speed * 0.6,
        40,
        this.worldWidth - 40,
      );
      this.y += intent.moveY * this.speed * 0.6;
    }

    if (intent.wantsAttack) {
      this.spawnAttackHitbox(intent.attackType);
    }

    if (intent.wantsSummon) {
      this.scene.events.emit('boss-wants-summon', { bossX: this.x, bossY: this.y });
    }

    const moving = intent.moveX !== 0 || intent.moveY !== 0;
    this.syncAnim({ moving, attacking: intent.wantsAttack });
  }

  private spawnAttackHitbox(attackType: string | null): void {
    if (!this.combatSystem) return;
    const spec =
      attackType !== null && attackType in BOSS_HIT_SPECS
        ? BOSS_HIT_SPECS[attackType]
        : DEFAULT_HIT_SPEC;
    const hbX = this.x + this.facing * (this.bodyW / 2 + ATTACK_HB_W / 2);
    const box = boxFromCenter(hbX, this.y, ATTACK_HB_W, ATTACK_HB_H);
    this.combatSystem.spawn(new Hitbox(this, box, { ...spec }, ATTACK_ACTIVE_FRAMES));
  }
}
